import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, FileText, Cookie, Info, AlertTriangle, Send } from 'lucide-react';
import { PolicyDocumentViewer } from './PolicyDocumentViewer';

interface PolicyAcknowledgmentModalProps {
  open: boolean;
  requiresParentalConsent: boolean;
  onSubmit: (data: {
    privacyPolicyAccepted: boolean;
    tosAccepted: boolean;
    cookiesAccepted: boolean;
    dataRightsAcknowledged: boolean;
    isMinorAcknowledgmentOnly?: boolean;
  }) => Promise<{ success: boolean; error?: any }>;
  isSubmitting?: boolean;
}

export const PolicyAcknowledgmentModal = ({
  open,
  requiresParentalConsent,
  onSubmit,
  isSubmitting = false
}: PolicyAcknowledgmentModalProps) => {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [dataRightsAccepted, setDataRightsAccepted] = useState(false);
  
  const [viewingPolicy, setViewingPolicy] = useState<'privacy' | 'tos' | null>(null);

  const allRequiredChecked = privacyAccepted && 
    tosAccepted && 
    cookiesAccepted && 
    dataRightsAccepted;

  const handleSubmit = async () => {
    if (!allRequiredChecked) return;

    const result = await onSubmit({
      privacyPolicyAccepted: privacyAccepted,
      tosAccepted: tosAccepted,
      cookiesAccepted: cookiesAccepted,
      dataRightsAcknowledged: dataRightsAccepted,
      isMinorAcknowledgmentOnly: requiresParentalConsent
    });

    if (!result.success) {
      console.error('Failed to submit acknowledgment:', result.error);
    }
  };

  // Different text for minors vs adults
  const getAcknowledgmentText = (policyName: string) => {
    if (requiresParentalConsent) {
      return `I have read and understand the ${policyName}`;
    }
    return `I have read and agree to the ${policyName}`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] overflow-hidden bg-card border-border"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-5 w-5 md:h-6 md:w-6 text-primary" />
              {requiresParentalConsent ? 'Parent Approval Required' : 'Legal Agreements Required'}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {requiresParentalConsent 
                ? 'Since you are under 13, your parent or guardian must approve your use of Players Lockeroom.'
                : 'Before accessing Players Lockeroom, please review and acknowledge our legal agreements.'}
            </DialogDescription>
          </DialogHeader>

          {/* Minor Notice Banner */}
          {requiresParentalConsent && (
            <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-foreground">Important: You Cannot Consent For Yourself</p>
                <p className="text-muted-foreground mt-1">
                  Under COPPA law, users under 13 cannot agree to legal terms on their own. 
                  After you acknowledge reading these policies, your parent must complete their approval 
                  on their own account using your Connection Code.
                </p>
              </div>
            </div>
          )}

          <ScrollArea className="max-h-[calc(85vh-280px)] md:max-h-[calc(90vh-320px)] pr-4 flex-1">
            <div className="space-y-4 md:space-y-6">
              {/* Privacy Policy */}
              <div className="space-y-3 p-3 md:p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy-policy"
                    checked={privacyAccepted}
                    onCheckedChange={(checked) => setPrivacyAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="privacy-policy" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Privacy Policy
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getAcknowledgmentText('')}{' '}
                      <button
                        type="button"
                        onClick={() => setViewingPolicy('privacy')}
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </button>
                      , including how personal data will be collected, used, and protected.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms of Service */}
              <div className="space-y-3 p-3 md:p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms-of-service"
                    checked={tosAccepted}
                    onCheckedChange={(checked) => setTosAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="terms-of-service" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Terms of Service
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getAcknowledgmentText('')}{' '}
                      <button
                        type="button"
                        onClick={() => setViewingPolicy('tos')}
                        className="text-primary hover:underline"
                      >
                        Terms of Service
                      </button>
                      , including the rules for using the platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cookie Consent */}
              <div className="space-y-3 p-3 md:p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="cookies"
                    checked={cookiesAccepted}
                    onCheckedChange={(checked) => setCookiesAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="cookies" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <Cookie className="h-4 w-4 text-primary" />
                      Cookie {requiresParentalConsent ? 'Notice' : 'Consent'}
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I {requiresParentalConsent ? 'understand' : 'consent to'} the use of cookies and similar technologies for site functionality, analytics, and notifications.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Rights */}
              <div className="space-y-3 p-3 md:p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="data-rights"
                    checked={dataRightsAccepted}
                    onCheckedChange={(checked) => setDataRightsAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="data-rights" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <Info className="h-4 w-4 text-primary" />
                      Data Rights Acknowledgment
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I understand {requiresParentalConsent ? 'my parent/guardian has' : 'my'} rights to access, correct, export, and delete personal data as outlined in the Privacy Policy.
                    </p>
                  </div>
                </div>
              </div>

              {/* Instructions for Minors */}
              {requiresParentalConsent && (
                <div className="space-y-3 p-3 md:p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="text-sm font-medium text-foreground">What happens next?</h4>
                  <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                    <li>After clicking below, you'll see your Connection Code</li>
                    <li>Share this code with your parent/guardian</li>
                    <li>Your parent enters the code on their dashboard to approve</li>
                    <li>Once they approve, you can access Players Lockeroom!</li>
                  </ol>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="flex-shrink-0 flex justify-end gap-3 pt-3 md:pt-4 border-t border-border">
            <Button
              onClick={handleSubmit}
              disabled={!allRequiredChecked || isSubmitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm md:text-base"
            >
              {isSubmitting 
                ? 'Processing...' 
                : requiresParentalConsent 
                  ? (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      I Acknowledge - Send Parent Alert
                    </>
                  )
                  : 'I Agree - Continue'}
            </Button>
          </div>

          <div className="flex-shrink-0 text-xs text-muted-foreground text-center">
            {requiresParentalConsent 
              ? 'By clicking "I Acknowledge - Send Parent Alert", a notification will be sent to your connected parent. You will see your Connection Code to share with them.'
              : 'By continuing, you acknowledge that you have read, understood, and agree to be bound by these agreements.'}
          </div>
        </DialogContent>
      </Dialog>

      {/* Policy Viewer Dialog */}
      {viewingPolicy && (
        <PolicyDocumentViewer
          policyType={viewingPolicy}
          open={!!viewingPolicy}
          onClose={() => setViewingPolicy(null)}
        />
      )}
    </>
  );
};
